import { getCookie, setCookie } from 'acces-impot-utils/lib/cookie'
import { getApiClient } from '@/api/client'
import LINK_PENDING_PROMO_GQL from '@/api/mutations/link-pending-promo.gql'
import { MESSAGE_TYPE_MAP } from '@/store/flash/flash'

const PROMO_URL_PARAM = 'promo'
const PROMO_COOKIE_KEY = 'acci_pending_promo_code'
const PROMO_COOKIE_EXPIRY_DAYS = 30

/**
 * Get the pending promo code from the cookie
 * @param {{ req?: import('http').IncomingMessage }} options
 * @returns {string}
 */
export function getPendingPromoCode(options = {}) {
  return getCookie({ req: options.req, key: PROMO_COOKIE_KEY })
}

/**
 * Clear the pending promo code cookie
 */
export function clearPendingPromoCode() {
  if (typeof document === 'undefined') return

  setCookie({
    key: PROMO_COOKIE_KEY,
    value: '',
    daysBeforeExpiration: -1,
  })
}

/**
 * Try to link pending promo code - backend will validate authentication and promo
 * @param {Pick<import('@nuxt/types').Context, 'app' | 'store'>} context
 * @param {string} promoCode
 */
export async function tryLinkPendingPromo(context, promoCode) {
  const app = context.app
  const dispatch = context.store?.dispatch || context.dispatch

  try {
    const client = getApiClient(app)
    if (!client) return

    const language = app.i18n?.locale || app.i18n?.language || 'fr'
    const result = await client.mutate({
      mutation: LINK_PENDING_PROMO_GQL,
      variables: { code: promoCode, language },
    })

    const payload = result?.data?.linkPendingPromoCode
    if (payload?.success) {
      clearPendingPromoCode()
    }
    if (payload?.message?.text) {
      dispatch?.('flash/addMessage', {
        type: MESSAGE_TYPE_MAP[payload.message.type],
        text: payload.message.text,
        isAutoHiding: false,
      })
    }
  } catch {
    // Silently ignore
    // - user may not be logged in
    // - promo may not exist or be exhausted
  }
}

/**
 * Extract promo code from URL, store in cookie, and try to link if logged in
 * @param {import('@nuxt/types').Context} context
 */
function trackPromoFromUrl(context) {
  const { query, route, app } = context

  const promoCode = query[PROMO_URL_PARAM] || route.query[PROMO_URL_PARAM]
  if (!promoCode) return

  setCookie({
    key: PROMO_COOKIE_KEY,
    value: String(promoCode),
    daysBeforeExpiration: PROMO_COOKIE_EXPIRY_DAYS,
  })

  if (process.client) {
    setTimeout(() => {
      const newQuery = { ...route.query }
      delete newQuery[PROMO_URL_PARAM]

      app.router.replace({ path: route.path, query: newQuery }).catch(() => {})
    }, 0)

    tryLinkPendingPromo(context, String(promoCode))
  }
}

export default context => {
  trackPromoFromUrl(context)
}
